import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { ArrowLeft, Settings as SettingsIcon, ChevronRight, User, Palette, Info, Trash2, LogOut } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import React from "react";
import { useAuthStore } from "@/features/auth/auth-store";
import { usePokerStore } from "@/features/poker/poker-store";

// --- Reusable Section Component ---
interface SettingsSectionProps {
  title: string;
  children: React.ReactNode;
}

const SettingsSection: React.FC<SettingsSectionProps> = ({ title, children }) => (
  <section>
    <h2 className="text-sm font-semibold text-muted-foreground mb-2 px-1 uppercase tracking-wider">{title}</h2>
    <Card className="p-0 overflow-hidden">
      <div className="divide-y">
        {children}
      </div>
    </Card>
  </section>
);

// --- Reusable Row Component ---
interface SettingsRowProps {
  icon: React.ElementType;
  title: string;
  children?: React.ReactNode;
  isLink?: boolean;
}

const SettingsRow: React.FC<SettingsRowProps> = ({ icon: Icon, title, children, isLink = true }) => (
    <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
            <Icon className="w-5 h-5 text-muted-foreground" />
            <p className="font-semibold">{title}</p>
        </div>
        <div className="flex items-center gap-2">
            {children}
            {isLink && <ChevronRight className="w-5 h-5 text-muted-foreground" />}
        </div>
    </div>
);


const Settings = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const clearPokerSession = usePokerStore((s) => s.clearSession);

  const handleLogout = () => {
    clearPokerSession();
    logout();
    navigate("/login");
  };

  const handleClearCache = () => {
      toast({
          title: t('settings_app_clear_cache_toast'),
      });
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="p-4 space-y-6 max-w-screen-xl mx-auto">
        {/* Header */}
        <header className="flex items-center gap-4">
          <Button asChild variant="ghost" size="icon" className="shrink-0">
            <Link to="/profile">
              <ArrowLeft className="w-6 h-6" />
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 p-3 rounded-xl text-primary">
              <SettingsIcon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{t('settings_title')}</h1>
              <p className="text-sm text-muted-foreground">{t('settings_subtitle')}</p>
            </div>
          </div>
        </header>

        {/* Account Section */}
        <SettingsSection title={t('settings_section_account')}>
            <SettingsRow icon={User} title={t('settings_account_username', 'Username')} isLink={false}>
                <p className="text-sm text-muted-foreground">{user?.username || "—"}</p>
            </SettingsRow>
            <SettingsRow icon={User} title={t('settings_account_email')} isLink={false}>
                <p className="text-sm text-muted-foreground">{user?.email || "—"}</p>
            </SettingsRow>
        </SettingsSection>

        {/* Appearance Section */}
        <SettingsSection title={t('settings_section_appearance')}>
             <SettingsRow icon={Palette} title={t('settings_appearance_language')} isLink={false}>
                 <Select value={SUPPORTED_LANGUAGES.find(l => i18n.language.startsWith(l.code))?.code ?? 'en'} onValueChange={(lang) => i18n.changeLanguage(lang)}>
                    <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder={t('settings_appearance_language')} />
                    </SelectTrigger>
                    <SelectContent>
                        {SUPPORTED_LANGUAGES.map((lang) => (
                          <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </SettingsRow>
        </SettingsSection>

        {/* Application Section */}
         <SettingsSection title={t('settings_section_app')}>
            <SettingsRow icon={Info} title={t('settings_app_version')} isLink={false}>
                <p className="text-sm text-muted-foreground">1.0.0</p>
            </SettingsRow>
             <button className="w-full text-left hover:bg-muted/50 transition-colors" onClick={handleClearCache}>
                <SettingsRow icon={Info} title={t('settings_app_clear_cache')} isLink={false} />
            </button>
        </SettingsSection>

        {/* Session */}
        <SettingsSection title={t('settings_section_session', 'Session')}>
            <button className="w-full text-left hover:bg-muted/50 transition-colors" onClick={handleLogout}>
                <SettingsRow icon={LogOut} title={t('settings_logout', 'Log out')} isLink={false} />
            </button>
        </SettingsSection>

        {/* Danger Zone */}
        <SettingsSection title={t('settings_section_danger')}>
            <button className="w-full text-left hover:bg-destructive/10 transition-colors text-destructive">
                <SettingsRow icon={Trash2} title={t('settings_delete_account')} isLink={false} />
            </button>
        </SettingsSection>
      </div>
    </div>
  );
};

export default Settings;
